import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link, useParams } from 'react-router-dom';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import toast, { Toaster } from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import countries from '../../data/countries.json';
import styles from '../signup.module.css';
import Navbar from '../../components/navbar';
import isLoggedIn from '../../utils/isLoggedIn';
import { convertEntitiesDeep } from '../../utils/convertEntities';

const UpdateSupplier = () => {
  // Initial state and variables ---------------------------------------------------\\

  const { id } = useParams();
  const url = `${process.env.REACT_APP_API}/admin/quantila/supplier/${id}`;
  const { token } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [feedback, setFeedback] = useState('');
  const [apiErrs, setApierrs] = useState([]);
  const [spinner, setSpinner] = useState(false);
  const [locked, setLock] = useState(true);
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState();

  // Fetch the supplier to update ---------------------------------------------------\\
  useEffect(() => {
    isLoggedIn(navigate, dispatch, `/quantila/supplier/update/${id}`);
    let active = true;

    (async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/admin/quantila/supplier/${id}`,
        { headers: { authorization: `Bearer ${token}` } },
      );
      if (active) {
        setSupplier(convertEntitiesDeep(response.data));
        setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [id]);

  // UseEffect to populate form fields. ---------------------------------------------------\\
  useEffect(() => {
    reset(supplier);
  }, [loading]);

  // Handle update ---------------------------------------------------\\
  const handleUpdate = async (data) => {
    const {
      name,
      siret,
      country,
      city,
      postcode,
      address,
      contact,
      email,
      website,
      dg,
      ca,
    } = data;

    try {
      const newSupplier = {
        name,
        siret,
        country,
        city,
        postcode,
        address,
        contact,
        email,
        website,
        dg,
        ca,
      };
      setSpinner(true);
      const response = await axios.put(`${url}`, newSupplier, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        toast.success(
          convertEntitiesDeep(response.data.message) ||
            'Supplier was successfully updated.',
          {
            duration: 3500,
          },
        );
        setSpinner(false);
        setApierrs([]);
        setLock(true);
        setTimeout(() => navigate(`/quantila/supplier/${id}`), 4000);
      } else {
        setFeedback(
          `Error Code: ${response.status}. \n OOOOPS.. ${convertEntitiesDeep(response.data.message)}`,
        );
      }
    } catch (error) {
      setApierrs(convertEntitiesDeep(error.response?.data?.errors || []));
      setSpinner(false);
    }
  };

  // Map API Errors ---------------------------------------------------\\
  const mapErrs = (obj) => {
    const keys = Object.keys(obj);
    return (
      <p className='help-block text-danger my-2'>
        {`${keys[0]}: `}
        {obj[keys[0]]}
      </p>
    );
  };

  // Rendered page ---------------------------------------------------\\
  return loading ? (
    // Waiting data loading after async request...
    <>
      <Helmet>
        <title>Badgers SAS: Update supplier</title>
        <meta name='page' content='update quantila supplier' />
      </Helmet>
      <div className='loading'>
        <img src='/img/loading.gif' alt='Loading' className='loading-img' />
      </div>
    </>
  ) : (
    <>
      <Helmet>
        <title>{`Badgers SAS: Update ${supplier?.name}`}</title>
        <meta name='page' content='update quantila supplier' />
      </Helmet>
      <Navbar displayMob={true} />
      <div className={styles.pan}>
        <Toaster position='top-center' reverseOrder={false} />
        <form
          onSubmit={handleSubmit(handleUpdate)}
          className='row g-3'
          action=''
        >
          {/* ----------------------- */}
          <div className=''>
            <Link className='page-links' to='/'>
              <FontAwesomeIcon icon='home' />{' '}
            </Link>
            <span className='link-span'>
              <FontAwesomeIcon icon='angle-right' />
            </span>
            <Link className='page-links' to='/quantila/suppliers'>
              SUPPLIERS{' '}
            </Link>
            <span className='link-span'>
              <FontAwesomeIcon icon='angle-right' />
            </span>
            <Link className='page-links' to={`/quantila/supplier/${id}`}>
              {`${supplier?.name?.toUpperCase()} `}
            </Link>
            <span className='link-span'>
              <FontAwesomeIcon icon='angle-right' />
            </span>
            <span className='link-span'>UPDATE SUPPLIER</span>
          </div>
          {/* ----------------------- */}
          <div className='col-12 d-flex justify-content-center my-4'>
            <h5 className='h5'>
              Update Supplier <FontAwesomeIcon icon='building' />
            </h5>
          </div>
          <div className={`${styles.card} row`}>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={supplier?.name}
                readOnly={locked}
                className='form-control'
                type='text'
                name='name'
                id='name'
                placeholder='Supplier Name'
                {...register('name', {
                  required: 'Please enter the supplier name.',
                  maxLength: 60,
                  minLength: 2,
                })}
              />
              {errors?.name && (
                <p className='help-block text-danger my-2'>
                  {errors.name?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={supplier?.siret}
                readOnly={locked}
                className='form-control'
                type='text'
                name='siret'
                id='siret'
                placeholder='SIRET, Registration Number'
                {...register('siret', {
                  maxLength: 30,
                  minLength: 3,
                })}
              />
              {errors?.siret && (
                <p className='help-block text-danger my-2'>
                  {errors.siret?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={supplier?.email}
                readOnly={locked}
                className='form-control'
                type='email'
                name='email'
                id='email'
                placeholder='Email Address'
                {...register('email', {})}
              />
              {errors?.email && (
                <p className='help-block text-danger my-2'>
                  {errors.email?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={supplier?.contact}
                readOnly={locked}
                className='form-control'
                type='text'
                name='contact'
                id='contact'
                placeholder='Contact Number'
                {...register('contact', {})}
              />
              {errors?.contact && (
                <p className='help-block text-danger my-2'>
                  {errors.contact?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={supplier?.address}
                readOnly={locked}
                className='form-control'
                type='text'
                name='address'
                id='address'
                placeholder='Supplier street address'
                {...register('address', {})}
              />
              {errors?.address && (
                <p className='help-block text-danger my-2'>
                  {errors.address?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={supplier?.postcode}
                readOnly={locked}
                className='form-control'
                type='text'
                name='postcode'
                id='postcode'
                placeholder='Post Code'
                {...register('postcode', {})}
              />
              {errors?.postcode && (
                <p className='help-block text-danger my-2'>
                  {errors.postcode?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={supplier?.city}
                readOnly={locked}
                className='form-control'
                type='text'
                name='city'
                id='city'
                placeholder='City or State'
                {...register('city', {})}
              />
              {errors?.city && (
                <p className='help-block text-danger my-2'>
                  {errors.city?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <select
                defaultValue={supplier?.country}
                id='country'
                name='country'
                className='form-select form-control'
                {...register('country', {})}
              >
                <option className='default-select' value=''>
                  Select Supplier's Location
                </option>
                {countries.map((obj, idx) => (
                  <option
                    value={obj.name}
                    key={idx}
                    selected={obj.name === supplier?.country}
                  >
                    {obj.name}
                  </option>
                ))}
              </select>
              {errors?.country && (
                <p className='help-block text-danger my-2'>
                  {errors.country?.message}
                </p>
              )}
            </div>

            <div className='my-2 col-md-6'>
              <input
                defaultValue={supplier?.website}
                readOnly={locked}
                className='form-control'
                type='url'
                name='website'
                id='website'
                placeholder='Website'
                {...register('website', {})}
              />
              {errors?.website && (
                <p className='help-block text-danger my-2'>
                  {errors.website?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={supplier?.dg}
                readOnly={locked}
                className='form-control'
                type='text'
                name='dg'
                id='dg'
                placeholder='Key Person'
                {...register('dg', {})}
              />
              {errors?.dg && (
                <p className='help-block text-danger my-2'>
                  {errors.dg?.message}
                </p>
              )}
            </div>
            <div className='my-2 col-md-6'>
              <input
                defaultValue={supplier?.ca}
                readOnly={locked}
                className='form-control'
                type='text'
                name='ca'
                id='ca'
                placeholder='Annual Turnover'
                {...register('ca', {})}
              />
              {errors?.ca && (
                <p className='help-block text-danger my-2'>
                  {errors.ca?.message}
                </p>
              )}
            </div>
            <div>
              <FormControlLabel
                control={
                  <Switch
                    checked={!locked}
                    onChange={(evt) => setLock(!evt.target.checked)}
                  />
                }
                label='Modify'
              />
            </div>
            {apiErrs.validation && (
              <p className='help-block text-danger my-2'>
                {errors.validation?.message}
              </p>
            )}
            {apiErrs.length > 0 && apiErrs.map((obj) => mapErrs(obj))}
            <div className='newsletter'>
              <p className='help-block text-danger my-2'>{feedback}</p>
            </div>
            <div className='row justify-content-center mt-4'>
              {spinner ? (
                <button type='' className={`btn ${styles.btnsignup}`}>
                  Sending data ..
                  <div
                    className='spinner-grow spinner-grow-sm'
                    role='status'
                  ></div>
                </button>
              ) : (
                <button
                  type='submit'
                  disabled={locked}
                  className={`btn ${styles.btnsignup}`}
                >
                  Update Supplier <FontAwesomeIcon icon='building' />
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default UpdateSupplier;
